import type { StockAdjustment, ReasonCode, StockAdjustResult, InvOnhand } from './types';

/**
 * #209 Discrepancias Half B (Task 7) — the "Ajustar existencia" write +
 * its read support + the transparency log.
 *
 * postStockAdjust mirrors commitDrift.ts for the write: read the
 * `pacebi_csrf` cookie, echo it via `X-CSRF-Token`, and mint a fresh
 * idempotency_key per call — the server de-dupes by this key
 * (registerStockAdjustRoute in handlers_stock_adjust.go replays the prior
 * result instead of calling the write verb again). That only catches an
 * identical RETRANSMISSION of the same call (e.g. a network-level retry of
 * an in-flight/lost-response request), not a double-click: this function
 * mints a brand-new key on every invocation, so two independent clicks
 * carry two different keys and neither is recognized as a duplicate of the
 * other. The actual double-click guard is Discrepancias.tsx's `adjusting`
 * state, which disables the confirm button for the duration of the
 * in-flight request.
 *
 * StockAdjustResult is FLAT — {"sid","old_int","new_int"} — NOT a
 * {"ok":true,"data":{...}} envelope: the Go handler writes the write
 * daemon's echoed response verbatim on success, and HTTP status (not a body
 * "ok" field) is the success/failure signal here. See types.ts's doc
 * comment on StockAdjustResult for the full rationale.
 */

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

export async function postStockAdjust(
  sid: number,
  line: string,
  part: string,
  newQty: number,
  reasonCode: string,
): Promise<StockAdjustResult> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/stock-adjust', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({
      sid, line, part, new_qty: newQty, reason_code: reasonCode,
      idempotency_key: crypto.randomUUID(),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as StockAdjustResult;
}

/** GET /api/stock-adjustments — the "Ajustes de existencia" transparency
 *  log. Gated owner-only server-side via adjust:post (user decision,
 *  2026-08-31) — the same permission that gates postStockAdjust below, not
 *  every authenticated user on this page. */
export async function fetchStockAdjustments(sid?: number, from?: string, to?: string): Promise<StockAdjustment[]> {
  const q = new URLSearchParams();
  if (sid != null) q.set('sid', String(sid));
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const res = await fetch(`/api/stock-adjustments?${q}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = await res.json();
  return (j.rows ?? []) as StockAdjustment[];
}

/** GET /api/reason-codes — the adjust-form reason dropdown source. This
 *  site's ReasonCode master carries zero CodeType="I" rows today (Task 0
 *  recon), so this typically resolves to [] and the form falls back to a
 *  free-text reason input; it picks up real codes automatically if the
 *  master is ever populated. */
export async function fetchReasonCodes(): Promise<ReasonCode[]> {
  const res = await fetch('/api/reason-codes', { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = await res.json();
  return (j.codes ?? []) as ReasonCode[];
}

/** GET /api/inv-onhand — current on-hand for a single part, used to prefill
 *  the Ajustar suggestion (on_hand - residual). */
export async function fetchInvOnhand(sid: number, line: string, part: string): Promise<number> {
  const q = new URLSearchParams({ sid: String(sid), line, part });
  const res = await fetch(`/api/inv-onhand?${q}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = (await res.json()) as InvOnhand;
  return j.on_hand ?? 0;
}
